import React, { useState, useEffect } from "react";
import {
    TextField, FormControl, InputLabel, Select, MenuItem, Button, Box, Typography,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, Chip,FormControlLabel,Switch,
    Alert, CircularProgress
} from "@mui/material";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import styled, { keyframes, css } from 'styled-components';
import { ThemeProvider } from '@mui/material/styles';
import recruiterTheme, {
    FONT_BODY, FONT_DISPLAY, COLORS, TYPE, RADIUS, SHADOW, CONTROL_HEIGHT,
} from '../../styles/recruiterTheme';
import { getEmailTemplate } from '../../emailTemplates/emailTemplate';
import {  salaryRanges, experiences, skills, secondarySkills } from '../shared/dropdown_values';
import useActivityLogger from '../../hooks/useActivityLogger';
import { Slider } from "@mui/material";
const LOCATIONS = ["Hyderabad", "Bangalore", "Chennai"];

// YYYY-MM-DD for the user's *local* today (toISOString() is UTC and is a day
// behind/ahead around midnight, which made the date picker's min wrong).
const localDateString = (d = new Date()) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// The backend stores endTime as the end of the picked day in IST (older JDs: UTC
// midnight); both keep the picked date as the UTC date part.
const toDateInputValue = (value) => {
    if (!value) return "";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "" : d.toISOString().split("T")[0];
};

const getStoredUser = () => {
    try {
        return JSON.parse(localStorage.getItem("rbotUser")) || {};
    } catch {
        return {};
    }
};

const fillTemplate = (text, vars) =>
    Object.entries(vars).reduce(
        (acc, [key, val]) => acc.split(`{{${key}}}`).join(val ?? ""),
        text || ""
    );
// ─── Animations ───────────────────────────────────────────────────────────────
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Page Wrapper ─────────────────────────────────────────────────────────────
// Rendered inline inside an already-padded dashboard container (HM dashboard tab,
// JD details, archived JDs), so it adds no outer page padding of its own.
const PageWrapper = styled.div`
  width: 100%;
  max-width: 960px;
  min-height: 100%;
  background: transparent;
  font-family: ${FONT_BODY};
  & *, & *::before, & *::after { box-sizing: border-box; }
  ${css`animation: ${fadeSlideIn} 0.4s ease both;`}
`;

// ─── Header block ─────────────────────────────────────────────────────────────
const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const BackButton = styled(IconButton)`
  && {
    background: linear-gradient(135deg, #F3EEF9, #E8DEF8);
    border-radius: ${RADIUS.sm}px;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    transition: all 0.2s;
    &:hover {
      background: linear-gradient(135deg, #E8DEF8, #D0BCFF);
      transform: scale(1.08);
    }
    svg { color: #4F378B; font-size: 20px; }
  }
`;

const FormTitle = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.page}px !important;
    line-height: 1.25 !important;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0 !important;
  }
`;

const FormSubtitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.body}px !important;
    line-height: 1.5 !important;
    color: ${COLORS.muted} !important;
    font-weight: 500 !important;
    margin: 4px 0 0 !important;
  }
`;

// ─── Section cards ────────────────────────────────────────────────────────────
const SectionCard = styled.div`
  background: #fff;
  border: 1px solid ${COLORS.border};
  border-radius: ${RADIUS.lg}px;
  box-shadow: ${SHADOW.card};
  padding: 24px;
  margin-bottom: 16px;
  min-width: 0;
`;

const SectionDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px;
  * + & { margin-top: 24px; }
`;

const SectionAccent = styled.div`
  width: 4px;
  height: 16px;
  border-radius: 4px;
  background: linear-gradient(135deg, #7C5CBF, #4F378B);
  flex-shrink: 0;
`;

const SectionLabel = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.cardTitle}px !important;
    line-height: 1.5 !important;
    color: ${COLORS.primaryDeep} !important;
  }
`;

// Two-column field grid (one column on narrow screens); long fields span both columns.
const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${p => p.$cols || 2}, minmax(0, 1fr));
  gap: 16px;
  align-items: start;
  @media (max-width: 720px) { grid-template-columns: minmax(0, 1fr); }
`;

const fullRow = { gridColumn: '1 / -1' };

// ─── Styled MUI overrides via sx ──────────────────────────────────────────────
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: `${RADIUS.sm}px`,
    fontFamily: FONT_BODY,
    fontSize: `${TYPE.body}px`,
    '&:not(.MuiInputBase-multiline)': { minHeight: CONTROL_HEIGHT.lg },
    '&:hover fieldset': { borderColor: '#7C5CBF' },
    '&.Mui-focused fieldset': { borderColor: '#6750A4', borderWidth: '2px' },
  },
  '& .MuiInputLabel-root': {
    fontFamily: FONT_BODY,
    fontSize: `${TYPE.body}px`,
    '&.Mui-focused': { color: '#6750A4' },
  },
  '& .MuiFormHelperText-root': {
    fontFamily: FONT_BODY,
    fontSize: `${TYPE.caption}px`,
    lineHeight: 1.5,
  },
};

// ─── Buttons ──────────────────────────────────────────────────────────────────
const PrimaryButton = styled(Button)`
  && {
    background: linear-gradient(135deg, #6750A4, #4F378B) !important;
    color: #fff !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.body}px !important;
    border-radius: ${RADIUS.sm}px !important;
    height: ${CONTROL_HEIGHT.md}px;
    padding: 0 24px !important;
    white-space: nowrap;
    text-transform: none !important;
    box-shadow: 0 4px 14px rgba(79,55,139,0.3) !important;
    transition: all 0.2s ease !important;
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 7px 20px rgba(79,55,139,0.4) !important;
    }
    &.Mui-disabled { opacity: 0.6; }
  }
`;

const GhostButton = styled(Button)`
  && {
    color: #6750A4 !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.body}px !important;
    text-transform: none !important;
    border-radius: ${RADIUS.sm}px !important;
    height: ${CONTROL_HEIGHT.md}px;
    padding: 0 16px !important;
    white-space: nowrap;
    &:hover { background: rgba(103,80,164,0.08) !important; }
  }
`;

const DialogOkButton = styled(PrimaryButton)`
  && { min-width: 96px; }
`;

// ─── Chip override (uniform 24px) ─────────────────────────────────────────────
const chipSx = {
  fontFamily: FONT_BODY,
  fontWeight: 600,
  fontSize: `${TYPE.caption}px`,
  height: 24,
  background: 'linear-gradient(135deg, #EDE7F6, #D0BCFF)',
  color: '#3D2B6B',
  borderRadius: `${RADIUS.sm}px`,
  '& .MuiChip-label': { px: '8px' },
  '& .MuiChip-deleteIcon': { fontSize: 16, color: '#7C5CBF', '&:hover': { color: '#4F378B' } },
};

const hintSx = { fontFamily: FONT_BODY, fontSize: TYPE.caption, lineHeight: 1.5, color: COLORS.muted };

// ─────────────────────────────────────────────────────────────────────────────
function JobDescriptionForm({ hiringManagerEmail: hiringManagerEmailProp, jd: jdProp, onClose: onCloseProp, fetchJds, onSaved }) {
    const navigate = useNavigate();
    const location = useLocation();
    // Rendered inline (props) or as the /job-description-form route (location.state / rbotUser).
    const jd = jdProp || location.state?.jd || null;
    const hiringManagerEmail =
        hiringManagerEmailProp || location.state?.hiringManagerEmail || getStoredUser().email || "";
    const onClose = () => {
        if (typeof onCloseProp === "function") onCloseProp();
        else if (window.history.state?.idx > 0) navigate(-1);
        else navigate("/hiring-dashboard", { state: { hiringManagerEmail } });
    };
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [recruiterEmails, setRecruiterEmails] = useState([]);
    const [interviewerEmails, setInterviewerEmails] = useState([]);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [clientNames, setClientNames] = useState([]);
    const [domains, setDomains] = useState([]);
    const [submitError, setSubmitError] = useState("");
    const [defaultCounts, setDefaultCounts] = useState({ mcq: 50, subjective: 30, coding: 15 });
    const [limits, setLimits] = useState({
        mcq:        { min: 10, max: 150 },
        subjective: { min: 5,  max: 100 },
        coding:     { min: 3,  max: 50  },
    });
    const clientNamesApiUrl = process.env.REACT_APP_API_CLIENTS_URL;
    const getRecruitersAndInterviewersApiUrl = process.env.REACT_APP_GET_RECRUITERS_INTERVIEWERS_API;
    const createJobDescriptionApiUrl = process.env.REACT_APP_CREATE_JOB_API;
    const editJDApiUrl = process.env.REACT_APP_EDIT_JD_API;
    const sendemailApiUrl = process.env.REACT_APP_SEND_EMAIL_API;
    const fileuploadApiUrl = process.env.REACT_APP_PARSE_JD_PDF_API;
    const processJDEmbeddingsApiUrl = process.env.REACT_APP_PROCESS_JDS_API;
    const processScreeningQuestionsUrl = process.env.REACT_APP_PROCESS_SCREENING_QUES_API;
    const domainsApiUrl = process.env.REACT_APP_DOMAINS_API;
    const screeningDefaultsUrl = process.env.REACT_APP_SCREENING_DEFAULTS_API;
    const programmingLanguages = ["Python", "C", "C++", "Java"];
    const programmingLevel = ["Easy", "Medium", "Hard"];

    const initialFormData = {
        companyName: "", 
        jdID: "",  
        jobDescription: "",
        jobRole: "",
        // programmingLanguage: "",
        // programmingLevel: "",
        // skills: [],
        // secondarySkills: [],
        experience: "",
        salaryRange: "",
        endTime: "",
        screeningTestPassPercentage: "",
        openPositions:"",
        preferredLocation:"",
        recruiterEmails: [],
        interviewerEmails: [],
        remarks: "",
        difficultyBoundaries: [40, 80], 
        useCustomCounts: false,
        generationCounts: { mcq: 50, subjective: 30, coding: 15 },
    };
    const levels = ["Junior", "Senior"];
const isBlank = (value) => value === null || value === undefined || !String(value).trim();
// derive job roles from fetched domains: "Junior AI/ML Engineer", "Senior AI/ML Engineer", ...
const jobRoles = domains.flatMap((domain) =>
    levels.map((level) => ({
        label: `${level} ${domain.label} Engineer`,
        value: `${level.toLowerCase()}_${domain.slug}`,
        slug: domain.slug,
    }))
);

    const [formData, setFormData] = useState(initialFormData);

    const { log } = useActivityLogger(hiringManagerEmail, 'hiringManager');

    // Re-seed the whole form whenever we switch JD (or switch edit -> create), so no
    // stale values leak between records. Keyed on _id so a parent re-render with a
    // fresh-but-equal jd object doesn't wipe in-progress edits.
    useEffect(() => {
        setSubmitted(false);
        setFormSubmitted(false);
        setSubmitError("");
        if (!jd) {
            setFormData({ ...initialFormData, generationCounts: defaultCounts });
            return;
        }
        const recruiterEmailsList = Array.isArray(jd.recruiterContacts)
            ? jd.recruiterContacts.map((recruiter) => recruiter?.email).filter(Boolean) : [];
        const interviewerEmailsList = Array.isArray(jd.interviewerContacts)
            ? jd.interviewerContacts.map((interviewer) => interviewer?.email).filter(Boolean) : [];

        setFormData({
            ...initialFormData,
            companyName: jd.companyName ?? "",
            jdID: jd.jdID ?? "",
            jobDescription: jd.jobDescription ?? "",
            jobRole: jd.jobRole ?? "",
            domain: jd.domain ?? "",
            department: jd.department ?? "",
            programmingLanguage: jd.programmingLanguage || "",
            programmingLevel: jd.programmingLevel || "",
            skills: Array.isArray(jd.skills) ? jd.skills : [],
            secondarySkills: Array.isArray(jd.secondarySkills) ? jd.secondarySkills : [],
            experience: jd.experience ?? "",
            screeningTestPassPercentage: jd.screeningTestPassPercentage ?? "",
            openPositions: jd.openPositions ?? "",
            preferredLocation: jd.preferredLocation ?? "",
            salaryRange: jd.salaryRange ?? "",
            endTime: toDateInputValue(jd.expiration_time),
            recruiterEmails: recruiterEmailsList,
            interviewerEmails: interviewerEmailsList,
            remarks: jd.remarks || "",
            difficultyBoundaries: boundariesFromDistribution(jd.difficulty_distribution),
            useCustomCounts: Boolean(jd.generation_counts),
            generationCounts: jd.generation_counts || defaultCounts,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jd?._id]);

    useEffect(() => {
        const fetchRecruitersAndInterviewers = async () => {
            try {
                const response = await axios.get(getRecruitersAndInterviewersApiUrl);
                const { recruiters, interviewers } = response.data;
                setRecruiterEmails(recruiters);
                setInterviewerEmails(interviewers);
            } catch (error) {
                console.error("Error fetching recruiters and interviewers:", error);
            }
        };
        fetchRecruitersAndInterviewers();
    }, []);

    useEffect(() => {
        const fetchClientNames = async () => {
            try {
                const token = localStorage.getItem("token");
                const response = await axios.get(
                    `${clientNamesApiUrl}/names/list`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setClientNames(response.data.data || []);
            } catch (error) {
                console.error("Error fetching client names:", error);
                // fallback — at least show the default
                setClientNames(["ZentreeLabs Pvt Ltd"]);
            }
        };
        fetchClientNames();
    }, [clientNamesApiUrl]);

useEffect(() => {
    const fetchDomains = async () => {
        try {
            const response = await axios.get(domainsApiUrl);
            setDomains(response.data.data || []);
        } catch (error) {
            console.error("Error fetching domains:", error);
            setDomains([]); 
        }
    };
    fetchDomains();
}, [domainsApiUrl]);

useEffect(() => {
    const fetchDefaults = async () => {
        try {
            const { data } = await axios.get(screeningDefaultsUrl);
            setDefaultCounts(data.defaults);
            setLimits(data.limits);
            setFormData((prev) =>
                prev.useCustomCounts ? prev : { ...prev, generationCounts: data.defaults }
            );
        } catch (error) {
            console.error("Error fetching screening defaults:", error);
        }
    };
    fetchDefaults();
}, [screeningDefaultsUrl]);

const boundariesFromDistribution = (dist) => {
    const easy = Number(dist?.easy);
    const medium = Number(dist?.medium);
    const hard = Number(dist?.hard);
    const valid =
        [easy, medium, hard].every(Number.isFinite) &&
        easy >= 0 && medium >= 0 && hard >= 0 &&
        easy + medium + hard === 100;
    return valid ? [easy, easy + medium] : [40, 80];   // fallback also covers the {0,0,0} placeholder
};

    const handleChange = (event, value = null) => {
        if (event.target) {
            const { name, value } = event.target;
            setFormData((prevState) => ({ ...prevState, [name]: value }));
        } else if (value !== null) {
            const name = event;
            setFormData((prevState) => ({ ...prevState, [name]: value }));
        }
    };

    const handlesecondskillsChange = (event, value) => {
        setFormData((prevState) => ({ ...prevState, secondarySkills: value }));
    };

    const sendEmailNotification = async (recipientEmail, subject, body) => {
        try {
            const loginUrl = process.env.REACT_APP_LOCAL_HOST_API;
            const emailBody = `${body}
                <p>Please click the link below to access the login page:</p>
                <p><a href="${loginUrl}">Click here to login</a></p>
                <p>Best regards,<br>Team</p>`;
            await axios.post(sendemailApiUrl, { candidateEmail: [recipientEmail], subject, body: emailBody });
        } catch (error) {
            console.error("Error sending email:", error);
        }
    };



    const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting || submitted) return;          // guard against double-click / Enter spam → duplicate JDs
    setFormSubmitted(true);
    setSubmitError("");

    const fail = (message) => { setSubmitError(message); return; };

    if (!formData.companyName || !formData.companyName.trim()) {
        return fail("Please select a company.");
    }
    // ── Validate jdID format (skip for legacy IDs that are unchanged on edit) ──
    const jdIDPattern = /^[A-Z]{3}-\d{8}$/;
    const jdIDUnchanged = jd && formData.jdID === jd.jdID;
    if (!jdIDUnchanged && !jdIDPattern.test(formData.jdID || "")) {
        return fail("JD ID must be in format: ABC-12345678 (3 uppercase letters, dash, 8 digits)");
    }
    if (isBlank(formData.jobDescription) || isBlank(formData.preferredLocation) || isBlank(formData.remarks)) {
        return fail("Job Description, Preferred Location, and Remarks cannot be blank or contain only spaces.");
    }
    if (isBlank(formData.jobRole)) return fail("Please select a job role.");
    if (isBlank(formData.experience)) return fail("Please enter or select the required experience.");
    if (isBlank(formData.salaryRange)) return fail("Please select a salary range.");
    if (!formData.endTime) return fail("Please choose a 'Valid Till' date.");
    if (formData.endTime < localDateString()) return fail("'Valid Till' date cannot be in the past.");

    const passPct = Number(formData.screeningTestPassPercentage);
    if (!Number.isFinite(passPct) || passPct < 1 || passPct > 100) {
        return fail("Screening test pass percentage must be between 1 and 100.");
    }
    const positions = Number(formData.openPositions);
    if (!Number.isInteger(positions) || positions < 1 || positions > 100) {
        return fail("Open positions must be a whole number between 1 and 100.");
    }
    if (!formData.interviewerEmails || formData.interviewerEmails.length === 0) {
        return fail("At least one interviewer is required.");
    }

    const [b1, b2] = formData.difficultyBoundaries;
    const difficultyDistribution = {
        easy: b1,
        medium: b2 - b1,
        hard: 100 - b2,
    };

    const generationCounts = {
    mcq:        Number(formData.generationCounts.mcq),
    subjective: Number(formData.generationCounts.subjective),
    coding:     Number(formData.generationCounts.coding),
        };

        const badCount = Object.entries(limits).find(([key, { min, max }]) => {
            const n = generationCounts[key];
            return !Number.isInteger(n) || n < min || n > max;
        });

        if (badCount) {
            const [key, { min, max }] = badCount;
            setSubmitError(`${key} count must be a whole number between ${min} and ${max}.`);
            return;
        }
    const formDataCopy = { ...formData, user_email: hiringManagerEmail,difficulty_distribution: difficultyDistribution,generation_counts: generationCounts, };

    setSubmitting(true);
    let responseData;
    try {
        let response;
        if (jd) {
            response = await axios.put(
                `${editJDApiUrl}/${encodeURIComponent(jd._id)}`,
                formDataCopy,
                { headers: { "Content-Type": "application/json" }, withCredentials: true }
            );
        } else {
            response = await axios.post(
                createJobDescriptionApiUrl,
                formDataCopy,
                { headers: { "Content-Type": "application/json" }, withCredentials: true }
            );
        }

        // axios throws on non-2xx by default, so no response.ok check needed —
        // but keep a defensive check in case that default was overridden elsewhere
        if (response.status < 200 || response.status >= 300) {
            throw new Error("Failed to submit form");
        }
        responseData = response.data || {};
    } catch (error) {
        console.error("Error submitting form:", error.response?.data || error.message);
        const backendMessage = error.response?.data?.message
            || (typeof error.response?.data === "string" ? error.response.data : "");
        setSubmitError(backendMessage || "Something went wrong while submitting the form. Please try again.");
        setSubmitting(false);
        return;
    }

    // ── Save succeeded: everything below is best-effort and must not surface as a submit error ──
    const savedJD = jd ? (responseData.existingJD || jd) : (responseData.result || {});
    const jdId = savedJD._id || jd?._id;
    const savedJdID = savedJD.jdID || formData.jdID;

    setSubmitted(true);
    setSubmitting(false);
    setShowSuccessDialog(true);
    if (typeof fetchJds === "function") {
        Promise.resolve(fetchJds()).catch((e) => console.error("Error refreshing JDs:", e));
    }
    if (typeof onSaved === "function") onSaved(savedJD);

    try {
        log(jd ? 'UPDATE_JD' : 'CREATE_JD', {
            jdId,
            targetName: formData.jobRole,
            metadata: {
              jdID:        savedJdID,
              companyName: formData.companyName,
              jobRole:     formData.jobRole,
              skills:      formData.skills,
              recruiters:  formData.recruiterEmails,
              interviewers: formData.interviewerEmails,
            }
          });
    } catch (e) {
        console.error("Error logging JD activity:", e);
    }

    if (jdId) {
        axios.post(
            processJDEmbeddingsApiUrl,
            { jdId },
            { headers: { "Content-Type": "application/json" }, withCredentials: true }
        ).catch(e => console.error("Error in processing JD embeddings:", e));
    }

    const { experience: exp, jobDescription: jd_description } = formDataCopy;
    const existingDifficultyBoundaries = boundariesFromDistribution(jd?.difficulty_distribution);
    const difficultyChanged =
        formData.difficultyBoundaries[0] !== existingDifficultyBoundaries[0] ||
        formData.difficultyBoundaries[1] !== existingDifficultyBoundaries[1];

    const existingCounts = jd?.generation_counts || defaultCounts;
    const countsChanged =
        generationCounts.mcq        !== existingCounts.mcq ||
        generationCounts.subjective !== existingCounts.subjective ||
        generationCounts.coding     !== existingCounts.coding;

    // Only regenerate screening questions if this is a new JD, or if any
    // technical fields changed when editing an existing JD.
    const technicalFieldsChanged =
        !jd ||
        exp !== jd.experience ||
        jd_description !== jd.jobDescription ||
        difficultyChanged ||
        countsChanged;

    if (technicalFieldsChanged && jdId) {
        // fire-and-forget: generation can take minutes and the user may already have closed the form
        axios.post(
            processScreeningQuestionsUrl,
            {
                jdId,
                experience: exp,
                jobDescription: jd_description,
                difficulty_distribution: difficultyDistribution,
                generation_counts: generationCounts,
            },
            { headers: { "Content-Type": "application/json" }, withCredentials: true }
        ).catch((error) => console.error(
            "Screening questions error:",
            error.response?.data || error.message
        ));
    }

    // Notify contacts. On edit, only people newly added to the JD are notified so
    // existing recruiters/interviewers aren't re-emailed on every update.
    const prevRecruiters = new Set((jd?.recruiterContacts || []).map((c) => c?.email));
    const prevInterviewers = new Set((jd?.interviewerContacts || []).map((c) => c?.email));
    const notify = (emails, previous, templateName) => {
        const template = getEmailTemplate(templateName);
        if (!template) return;
        (emails || [])
            .filter((email) => !previous.has(email))
            .forEach((email) => {
                const emailBody = fillTemplate(template.body, { jobRole: formData.jobRole, jobID: savedJdID });
                sendEmailNotification(email, template.subject, emailBody);
            });
    };
    notify(formData.recruiterEmails, prevRecruiters, 'recruiterNotification');
    notify(formData.interviewerEmails, prevInterviewers, 'interviewerNotification');
    };



    const handleFileChange = async (event) => {
        event.preventDefault();
        const input = event.target;
        const selectedFile = input.files?.[0];
        if (!selectedFile) return;
        const formuploadedData = new FormData();
        formuploadedData.append("file", selectedFile);
        setUploading(true);
        setSubmitError("");
        try {
            const response = await axios.post(fileuploadApiUrl, formuploadedData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const text = response.data?.text;
            if (!text) throw new Error("No text could be extracted from the file.");
            setFormData((prevState) => ({ ...prevState, jobDescription: text }));
        } catch (error) {
            console.error("Error:", error);
            setSubmitError(error.response?.data?.message || error.message || "Could not read the uploaded file.");
        } finally {
            setUploading(false);
            input.value = "";   // allow re-selecting the same file
        }
    };
    // keep any older free-text value selectable so editing old JDs doesn't show a blank field
const locationOptions =
    formData.preferredLocation && !LOCATIONS.includes(formData.preferredLocation)
        ? [formData.preferredLocation, ...LOCATIONS]
        : LOCATIONS;

    // show backend/validation errors about the JD ID on the field itself; everything else in the alert
    const jdIdError = !!submitError && /jd ?id|already exists|duplicate/i.test(submitError);

    const handleCloseSuccessDialog = () => {
        setShowSuccessDialog(false);
        setFormData({ ...initialFormData, generationCounts: defaultCounts });
        onClose();
    };

    return (
        <ThemeProvider theme={recruiterTheme}>
            <PageWrapper>
                {/* ── Header ── */}
                <HeaderRow>
                    <BackButton onClick={onClose}>
                        <ArrowBackIcon />
                    </BackButton>
                    <Box sx={{ minWidth: 0 }}>
                        <FormTitle>{jd ? "Update Job" : "Create New Job"}</FormTitle>
                        <FormSubtitle>
                            {jd ? "Edit the details below to update this job description" : "Fill in the details below to post a new job opening"}
                        </FormSubtitle>
                    </Box>
                </HeaderRow>

                <form onSubmit={handleSubmit}>

                    {/* ══ Card 1: Company Info + Job Details ══ */}
                    <SectionCard>
                        <SectionDivider>
                            <SectionAccent />
                            <SectionLabel>Company Info</SectionLabel>
                        </SectionDivider>

                        <FieldGrid>
                            {/* <TextField
                                id="companyName" type="text" name="companyName" label="Company Name"
                                required fullWidth sx={fieldSx}
                                onChange={handleChange} value={formData.companyName}
                            /> */}
                            <FormControl fullWidth required>
                                <Autocomplete
                                    id="companyName"
                                    size="small"
                                    options={["", ...clientNames]}   // clientNames already has ZentreeLabs first
                                    value={formData.companyName}
                                    onChange={(event, value) =>
                                        handleChange({ target: { name: 'companyName', value: value || "" } })
                                    }
                                    getOptionLabel={(option) =>
                                        option === "" ? "Select an option" : option
                                    }
                                    renderOption={(props, option) => (
                                        <li {...props} key={option}>
                                            {option === "" ? (
                                                <span style={{ color: COLORS.faint, fontFamily: FONT_BODY, fontSize: TYPE.body }}>
                                                    Select an option
                                                </span>
                                            ) : option}
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Company Name"
                                            placeholder="Select an option"
                                            required
                                            fullWidth
                                            sx={fieldSx}
                                        />
                                    )}
                                />
                            </FormControl>
                            <TextField
                                id="jdID"
                                type="text"
                                name="jdID"
                                label="JD ID (e.g. ZEN-12345678)"
                                required
                                fullWidth
                                size="small"
                                sx={fieldSx}
                                inputProps={{
                                    maxLength: 12,
                                    title: "Format: ABC-12345678 (3 letters, dash, 8 digits)"
                                }}
                                onChange={(e) => {
                                    const previousValue = formData.jdID || "";
                                    let value = e.target.value.toUpperCase();
                                    value = value.replace(/[^A-Z0-9-]/g, '');
                                    const isDeleting = value.length < previousValue.length;
                                    if (!isDeleting && value.length === 3 && !value.includes('-')) {
                                        value = value + '-';
                                    }
                                    handleChange({ target: { name: 'jdID', value } });
                                    if (submitError) setSubmitError("");   // clear the stale duplicate-ID error as soon as they edit it
                                }}
                                value={formData.jdID}
                                error={jdIdError}
                                helperText={jdIdError ? submitError : "Format: 3 letters – 8 digits (e.g. ZEN-12345678)"}
                            />
                        </FieldGrid>

                        {/* ── Job Details ── */}
                        <SectionDivider>
                            <SectionAccent />
                            <SectionLabel>Job Details</SectionLabel>
                        </SectionDivider>

                        <FieldGrid>
                            <TextField
                                id="jobDescription"
                                name="jobDescription"
                                label="Job Description and Required Skills"
                                placeholder="Enter the job description, responsibilities, required skills, technologies, experience, and qualifications..."
                                multiline
                                rows={6}
                                fullWidth
                                sx={{ ...fieldSx, ...fullRow }}
                                onChange={handleChange}
                                value={formData.jobDescription}
                                required
                                error={isBlank(formData.jobDescription) && formSubmitted}
                                helperText={
                                    isBlank(formData.jobDescription) && formSubmitted
                                        ? "Job description is required and cannot be only spaces."
                                        : "Include the job responsibilities, required technical skills, technologies, experience, and qualifications."
                                }
                            />

                            <TextField
                                id="fileInput"
                                type="file"
                                name="fileInput"
                                label="Upload Job Description File"
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    inputProps: {
                                        accept: ".pdf,.doc,.docx"
                                    }
                                }}
                                fullWidth
                                size="small"
                                sx={fieldSx}
                                disabled={uploading || submitting}
                                helperText={uploading ? "Extracting text from file…" : ""}
                                onChange={handleFileChange}
                            />

                            <FormControl fullWidth required>
                                <Autocomplete
                                    id="jobRole"
                                    size="small"
                                    options={jobRoles.map((role) => role.label)}
                                    value={formData.jobRole}
                                    onChange={(event, value) => {
                                        const match = jobRoles.find((r) => r.label === value);
                                        handleChange({ target: { name: 'jobRole', value: value || '' } });
                                        handleChange({ target: { name: 'domain', value: match ? match.slug : '' } });
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Job Role"
                                            placeholder="Enter or select job role" fullWidth required
                                            sx={fieldSx} />
                                    )}
                                />
                            </FormControl>
                        </FieldGrid>
                    </SectionCard>

                    {/* ══ Card 2: Compensation & Timeline ══ */}
                    <SectionCard>
                        <SectionDivider>
                            <SectionAccent />
                            <SectionLabel>Compensation & Timeline</SectionLabel>
                        </SectionDivider>

                        <FieldGrid>
                            <FormControl fullWidth required>
                                <Autocomplete
                                    id="experience" name="experience" freeSolo
                                    size="small"
                                    options={experiences.map((exp) => exp.label)}
                                    value={formData.experience}
                                    onChange={(event, value) => handleChange({ target: { name: 'experience', value } })}
                                    onInputChange={(event, newInputValue) => handleChange({ target: { name: 'experience', value: newInputValue } })}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Experience"
                                            placeholder="Enter or select experience" fullWidth required
                                            sx={fieldSx} />
                                    )}
                                />
                            </FormControl>

                            <FormControl fullWidth>
                                <Autocomplete
                                    id="salaryRange" name="salaryRange"
                                    size="small"
                                    options={salaryRanges.map((range) => range.label)}
                                    value={formData.salaryRange}
                                    onChange={(event, value) => {
                                        const newValue = value && !value.includes("LPA") ? `${value} LPA` : value;
                                        handleChange({ target: { name: 'salaryRange', value: newValue } });
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Salary Range"
                                            placeholder="Enter or select salary range" fullWidth
                                            required={formData.salaryRange === ""} sx={fieldSx} />
                                    )}
                                />
                            </FormControl>

                            <TextField
                                id="endTime" name="endTime" label="Valid Till" type="date"
                                InputLabelProps={{ shrink: true }} fullWidth size="small" sx={fieldSx}
                                onChange={handleChange}
                                inputProps={{ min: localDateString() }}
                                value={formData.endTime} required
                            />

                            <TextField
                                id="screeningTestPassPercentage" type="number"
                                name="screeningTestPassPercentage" label="Screening Test Pass Percentage"
                                required fullWidth size="small" sx={fieldSx}
                                onChange={handleChange} value={formData.screeningTestPassPercentage}
                                inputProps={{ min: 1, max: 100, step: 1, inputMode: "numeric" }}
                            />
                            <TextField
                                id="openPositions" type="number"
                                name="openPositions" label="Open Positions Available"
                                required fullWidth size="small" sx={fieldSx}
                                onChange={handleChange} value={formData.openPositions}
                                inputProps={{ min: 1, max: 100, step: 1, inputMode: "numeric" }}
                            />
                            <TextField
                                select
                                id="preferredLocation"
                                name="preferredLocation"
                                label="Preferred location"
                                required
                                fullWidth
                                size="small"
                                sx={fieldSx}
                                onChange={handleChange}
                                value={formData.preferredLocation || ""}
                                error={isBlank(formData.preferredLocation) && formSubmitted}
                                helperText={
                                    isBlank(formData.preferredLocation) && formSubmitted
                                        ? "Please select a preferred location."
                                        : ""
                                }
                            >
                                {locationOptions.map((city) => (
                                    <MenuItem key={city} value={city}>
                                        {city}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </FieldGrid>
                    </SectionCard>

                    {/* ══ Card 3: Team Assignment + Additional Info ══ */}
                    <SectionCard>
                        <SectionDivider>
                            <SectionAccent />
                            <SectionLabel>Team Assignment</SectionLabel>
                        </SectionDivider>

                        <FieldGrid>
                            <FormControl fullWidth>
                                <Autocomplete
                                    multiple
                                    id="recruiterEmail"
                                    size="small"
                                    options={recruiterEmails.map((r) => r.email)}
                                    value={formData.recruiterEmails || []}
                                    onChange={(event, value) =>
                                        handleChange({ target: { name: 'recruiterEmails', value } })
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Recruiter Email"
                                            placeholder="Select recruiter(s)"
                                            fullWidth
                                            sx={fieldSx}
                                        />
                                    )}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip key={option} label={option} size="small" {...getTagProps({ index })} sx={chipSx} />
                                        ))
                                    }
                                />
                            </FormControl>

                            <FormControl fullWidth>
                                <Autocomplete
                                    multiple
                                    id="interviewerEmail"
                                    size="small"
                                    options={interviewerEmails.map((i) => i.email)}
                                    value={formData.interviewerEmails || []}
                                    onChange={(event, value) =>
                                        handleChange({ target: { name: 'interviewerEmails', value } })
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Interviewer Email"
                                            placeholder="Select interviewer(s)"
                                            fullWidth
                                            sx={fieldSx}
                                            error={formData.interviewerEmails.length === 0 && formSubmitted}
                                            helperText={formData.interviewerEmails.length === 0 && formSubmitted ? "At least one interviewer is required" : ""}
                                        />
                                    )}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip key={option} label={option} size="small" {...getTagProps({ index })} sx={chipSx} />
                                        ))
                                    }
                                />
                            </FormControl>
                        </FieldGrid>

                        {/* ── Additional Info ── */}
                        <SectionDivider>
                            <SectionAccent />
                            <SectionLabel>Additional Info</SectionLabel>
                        </SectionDivider>

                        <FieldGrid>
                            <TextField
                                label="Remarks" name="remarks" value={formData.remarks}
                                onChange={handleChange} required fullWidth size="small"
                                sx={{ ...fieldSx, ...fullRow }}
                                error={isBlank(formData.remarks) && formSubmitted}
                                helperText={isBlank(formData.remarks) && formSubmitted ? "Remarks are required and cannot be only spaces." : ""}
                            />
                        </FieldGrid>
                    </SectionCard>

                    {/* ══ Card 4: Screening ══ */}
                    <SectionCard>
                        {/* ── Question Difficulty Split ── */}
                        <SectionDivider>
                            <SectionAccent />
                            <SectionLabel>Screening Question Difficulty</SectionLabel>
                        </SectionDivider>

                        {(() => {
                            const [b1, b2] = formData.difficultyBoundaries;
                            const easy = b1;
                            const medium = b2 - b1;
                            const hard = 100 - b2;

                            return (
                                <Box sx={{ px: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small, fontWeight: 600, color: '#2E7D32', fontVariantNumeric: 'tabular-nums' }}>
                                            Easy: {easy}%
                                        </Typography>
                                        <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small, fontWeight: 600, color: '#F9A825', fontVariantNumeric: 'tabular-nums' }}>
                                            Medium: {medium}%
                                        </Typography>
                                        <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small, fontWeight: 600, color: '#C62828', fontVariantNumeric: 'tabular-nums' }}>
                                            Hard: {hard}%
                                        </Typography>
                                    </Box>

                                    <Slider
                                        value={formData.difficultyBoundaries}
                                        onChange={(event, newValue) => {
                                            handleChange({ target: { name: 'difficultyBoundaries', value: newValue } });
                                        }}
                                        disableSwap
                                        min={0}
                                        max={100}
                                        step={5}
                                        sx={{
                                            height: 10,
                                            '& .MuiSlider-track': {
                                                background: 'linear-gradient(to right, #66BB6A, #FFCA28)',
                                                border: 'none',
                                            },
                                            '& .MuiSlider-rail': {
                                                background: '#EF5350',
                                                opacity: 1,
                                            },
                                            '& .MuiSlider-thumb': {
                                                width: 20,
                                                height: 20,
                                                background: '#4F378B',
                                                border: '3px solid #fff',
                                                boxShadow: '0 2px 8px rgba(79,55,139,0.4)',
                                                '&:hover, &.Mui-focusVisible': {
                                                    boxShadow: '0 0 0 8px rgba(79,55,139,0.16)',
                                                },
                                            },
                                        }}
                                    />

                                    <Typography sx={{ ...hintSx, mt: 0.5 }}>
                                        Drag the handles to adjust the difficulty split — always totals 100%.
                                    </Typography>
                                </Box>
                            );
                        })()}

                        <SectionDivider>
                            <SectionAccent />
                            <SectionLabel>Question Bank Size</SectionLabel>
                        </SectionDivider>

                        <Box>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={!formData.useCustomCounts}
                                        onChange={(e) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                useCustomCounts: !e.target.checked,
                                                generationCounts: e.target.checked ? defaultCounts : prev.generationCounts,
                                            }))
                                        }
                                        sx={{ '& .Mui-checked': { color: '#4F378B' } }}
                                    />
                                }
                                label={
                                    <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, fontWeight: 600, color: COLORS.ink }}>
                                        Use the standard bank size
                                    </Typography>
                                }
                            />

                            {!formData.useCustomCounts ? (
                                <Box sx={{ p: 2, background: '#F7F4FC', borderRadius: `${RADIUS.md}px`, mt: 1 }}>
                                    <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small, color: '#5C4A8A', fontVariantNumeric: 'tabular-nums' }}>
                                        {defaultCounts.mcq} multiple choice · {defaultCounts.subjective} theory · {defaultCounts.coding} coding
                                    </Typography>
                                    <Typography sx={{ ...hintSx, mt: 0.5 }}>
                                        Turn the switch off to set your own numbers for this job.
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <FieldGrid $cols={3} style={{ marginTop: 8 }}>
                                        {[
                                            { key: 'mcq',        label: 'Multiple choice', min: 10, max: 150 },
                                            { key: 'subjective', label: 'Theory',          min: 5,  max: 100 },
                                            { key: 'coding',     label: 'Coding',          min: 3,  max: 50  },
                                        ].map(({ key, label, min, max }) => (
                                            <TextField
                                                key={key}
                                                type="number"
                                                label={label}
                                                value={formData.generationCounts[key]}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        generationCounts: { ...prev.generationCounts, [key]: e.target.value },
                                                    }))
                                                }
                                                inputProps={{ min, max, step: 1 }}
                                                helperText={`${min}–${max} · standard ${defaultCounts[key]}`}
                                                fullWidth
                                                size="small"
                                                sx={fieldSx}
                                            />
                                        ))}
                                    </FieldGrid>

                                    {(() => {
                                        const total = Object.values(formData.generationCounts).reduce((a, b) => a + Number(b || 0), 0);
                                        return (
                                            <Typography sx={{ ...hintSx, mt: 1, color: total > 150 ? '#C62828' : COLORS.muted }}>
                                                {total} questions in total.{total > 150 && " Large banks take several minutes to generate."}
                                            </Typography>
                                        );
                                    })()}
                                </>
                            )}
                        </Box>
                    </SectionCard>

                    {/* ── Submit ── */}
                    {submitError && !jdIdError && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: `${RADIUS.md}px` }} onClose={() => setSubmitError("")}>
                            {submitError}
                        </Alert>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, mt: 3 }}>
                        <GhostButton type="button" onClick={onClose} disabled={submitting}>
                            Cancel
                        </GhostButton>
                        <PrimaryButton type="submit" disabled={submitting || submitted || uploading}>
                            {submitting && <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />}
                            {submitting
                                ? (jd ? "Updating…" : "Creating…")
                                : (jd ? "Update Job" : "Create Job")}
                        </PrimaryButton>
                    </Box>
                </form>

                {/* ── Success Dialog ── */}
                <Dialog
                    open={showSuccessDialog}
                    onClose={handleCloseSuccessDialog}
                    PaperProps={{
                        sx: {
                            borderRadius: `${RADIUS.lg}px`, padding: '8px',
                            boxShadow: '0 12px 40px rgba(79,55,139,0.2)',
                            fontFamily: FONT_BODY,
                        }
                    }}
                >
                    <DialogTitle sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, color: COLORS.primaryDeep, fontSize: TYPE.section }}>
                        🎉 Job Description Saved!
                    </DialogTitle>
                    <DialogContent>
                        <Typography sx={{ fontFamily: FONT_BODY, color: '#5C4A8A', fontSize: TYPE.body, lineHeight: 1.5, textAlign: 'center' }}>
                            You can now view the recently {jd ? "updated" : "created"} JD in the list of job descriptions.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ padding: '16px 24px' }}>
                        <DialogOkButton onClick={handleCloseSuccessDialog} autoFocus>
                            OK
                        </DialogOkButton>
                    </DialogActions>
                </Dialog>
            </PageWrapper>
        </ThemeProvider>
    );
}

export default JobDescriptionForm;
